// Utilidades comunes de la capa de compatibilidad con Firebase (navegador).
// Formato de los datos con el servidor:
//   fecha          -> {"__ts": milisegundos}
//   valor especial -> {"__fv": "...", "v": ...}

export class FirebaseError extends Error {
  constructor(code, message, details) {
    super(message || code);
    this.code = code;
    this.name = 'FirebaseError';
    this.details = details;
    this.customData = {};
  }
}

export class Timestamp {
  constructor(seconds, nanoseconds = 0) {
    this.seconds = Math.floor(seconds);
    this.nanoseconds = Math.floor(nanoseconds);
  }
  static now() { return Timestamp.fromMillis(Date.now()); }
  static fromDate(d) { return Timestamp.fromMillis(new Date(d).getTime()); }
  static fromMillis(ms) {
    const s = Math.floor(ms / 1000);
    return new Timestamp(s, Math.round((ms - s * 1000) * 1e6));
  }
  toMillis() { return this.seconds * 1000 + Math.floor(this.nanoseconds / 1e6); }
  toDate() { return new Date(this.toMillis()); }
  isEqual(o) { return o instanceof Timestamp && o.toMillis() === this.toMillis(); }
  valueOf() { return String(this.toMillis()).padStart(20, '0'); }
  toJSON() { return { seconds: this.seconds, nanoseconds: this.nanoseconds, type: 'firestore/timestamp/1.0' }; }
  toString() { return `Timestamp(seconds=${this.seconds}, nanoseconds=${this.nanoseconds})`; }
}

export class FieldValueSentinel {
  constructor(kind, v) { this.__fv = kind; this.v = v; }
  isEqual(o) { return o instanceof FieldValueSentinel && o.__fv === this.__fv; }
}

export class GeoPoint {
  constructor(latitude, longitude) { this.latitude = latitude; this.longitude = longitude; }
}

export function encode(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Timestamp) return { __ts: value.toMillis() };
  if (value instanceof Date) return { __ts: value.getTime() };
  if (value instanceof FieldValueSentinel) {
    return value.v === undefined ? { __fv: value.__fv } : { __fv: value.__fv, v: encode(value.v) };
  }
  if (value instanceof GeoPoint) return { __geo: [value.latitude, value.longitude] };
  if (value && value.type === 'document' && typeof value.path === 'string' && value.firestore) return { __ref: value.path };
  if (Array.isArray(value)) return value.map(v => (v === undefined ? null : encode(v)));
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function' && typeof value.seconds === 'number') return { __ts: value.toDate().getTime() };
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const e = encode(v);
      if (e !== undefined) out[k] = e;
    }
    return out;
  }
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  return value;
}

export function decode(value, makeRef) {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(v => decode(v, makeRef));
  const keys = Object.keys(value);
  if (keys.length === 1 && keys[0] === '__ts') return Timestamp.fromMillis(value.__ts);
  if (keys.length === 1 && keys[0] === '__geo') return new GeoPoint(value.__geo[0], value.__geo[1]);
  if (keys.length === 1 && keys[0] === '__ref') return makeRef ? makeRef(value.__ref) : value.__ref;
  const out = {};
  for (const [k, v] of Object.entries(value)) out[k] = decode(v, makeRef);
  return out;
}

// ---------- Conexión con la API ----------
let tokenProvider = null;
export function setTokenProvider(fn) { tokenProvider = fn; }

export async function apiCall(path, body, { method = 'POST', auth = true, raw = null, headers = {} } = {}) {
  const doFetch = async (forceRefresh) => {
    const h = { ...headers };
    if (!raw) h['Content-Type'] = 'application/json';
    if (auth && tokenProvider) {
      const t = await tokenProvider(forceRefresh);
      if (t) h.Authorization = `Bearer ${t}`;
    }
    return fetch(`/api${path}`, {
      method,
      headers: h,
      body: method === 'GET' ? undefined : (raw ?? JSON.stringify(body ?? {})),
      credentials: 'same-origin',
    });
  };
  let res;
  try {
    res = await doFetch(false);
    if (res.status === 401 && auth && tokenProvider) res = await doFetch(true);
  } catch (e) {
    throw new FirebaseError('unavailable', 'No se puede conectar con el servidor. Revisa tu conexión.');
  }
  let json = null;
  try { json = await res.json(); } catch { /* sin cuerpo */ }
  if (!res.ok) {
    const err = json?.error || {};
    throw new FirebaseError(err.code || 'internal', err.message || `Error ${res.status}`, err.details);
  }
  return json;
}
